import { useEffect, useMemo, useState } from "react";
import { resolveImage, onImgError } from "@/lib/images";
import CardImage from "@/components/CardImage";
import { Link, useSearchParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchTemples } from "@/lib/api";
import api from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { HERO_ART } from "@/lib/heroes";
import { stripHtml } from "@/lib/html";
import { MapPin, ArrowLeft, ArrowRight, Landmark, Compass } from "lucide-react";

const TempleCard = ({ t }) => (
  <Link to={`/temples/${t.slug}`} data-testid={`temple-card-${t.slug}`} className="group block overflow-hidden rounded-xl border border-line bg-[var(--surface)] transition-colors hover:border-[var(--gold)]">
    <CardImage src={resolveImage(t)} alt={t.name} onError={onImgError(t)} ratio="aspect-[4/3]" />
    <div className="p-5">
      <p className="font-deva text-lg text-ink deva-lh">{t.deva_name}</p>
      <h3 className="font-serif-latin text-lg text-ink">{t.name}</h3>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-3"><MapPin className="h-3.5 w-3.5" /> {t.location}, {t.state}</p>
      {t.deity && (
        <p className="mt-2 text-sm font-medium leading-snug text-saffron" data-testid={`temple-card-deity-${t.slug}`}>{t.deity}</p>
      )}
      <p className="mt-3 text-sm leading-relaxed text-ink-2 line-clamp-2">{stripHtml(t.description || t.significance)}</p>
    </div>
  </Link>
);

// A category/circuit tile on the Temples landing — image from a representative
// temple, with name, note and count. Links into the filtered circuit view.
const CircuitCard = ({ c, cover, count, pick }) => (
  <Link to={`/temples?circuit=${c.id}`} data-testid={`circuit-card-${c.id}`}
    className="group relative flex flex-col overflow-hidden rounded-xl border border-line bg-[var(--surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-xl">
    <div className="aspect-[16/10] w-full overflow-hidden bg-[var(--parchment)]">
      {cover ? (
        <img src={cover} alt={c.en} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[var(--saffron)]/10"><Landmark className="h-10 w-10 text-saffron/50" /></div>
      )}
    </div>
    <div className="flex flex-1 flex-col p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-deva text-xl text-ink deva-lh">{c.deva}</p>
          <p className="font-serif-latin text-base text-ink-2">{c.en}</p>
        </div>
        <span className="shrink-0 rounded-full border border-line bg-[var(--surface-alt)]/60 px-2.5 py-1 text-[11px] font-medium text-ink-3">{count} {pick("मंदिर", count === 1 ? "temple" : "temples")}</span>
      </div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-ink-2">{pick(c.note_hi || c.note, c.note)}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-saffron transition-transform group-hover:translate-x-0.5">{pick("दर्शन करें", "Explore")} <ArrowRight className="h-4 w-4" /></span>
    </div>
  </Link>
);

export default function Temples() {
  const { pick } = useLang();
  const [temples, setTemples] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("circuit") || "";

  useEffect(() => {
    fetchTemples().then(setTemples).catch(() => {});
    api.get("/circuits").then((r) => setCircuits(r.data)).catch(() => {});
  }, []);

  // circuit id -> { count, cover } derived from the temples
  const circuitMeta = useMemo(() => {
    const m = {};
    temples.forEach((t) => {
      const id = t.circuit || "major";
      if (!m[id]) m[id] = { count: 0, cover: "" };
      m[id].count += 1;
      if (!m[id].cover) { const img = resolveImage(t); if (img) m[id].cover = img; }
    });
    return m;
  }, [temples]);

  const shownCircuits = circuits.filter((c) => (circuitMeta[c.id]?.count || 0) > 0);
  const activeCircuit = circuits.find((c) => c.id === active);
  const activeTemples = active ? temples.filter((t) => (t.circuit || "major") === active) : [];

  return (
    <div data-testid="temples-page">
      <PageHero
        art={HERO_ART.devotion}
        overline={pick("भारत के मंदिर", "Temples of Bharat")}
        deva={activeCircuit ? activeCircuit.deva : "भारत के दिव्य मंदिर"}
        en={activeCircuit ? pick(activeCircuit.deva, activeCircuit.en) : pick("मंदिर परिक्रमा", "Sacred Temple Circuits")}
        tagline={activeCircuit ? pick(activeCircuit.note_hi || activeCircuit.note, activeCircuit.note) : pick("द्वादश ज्योतिर्लिंग, शक्तिपीठ, अष्टविनायक एवं भारत के महान वैष्णव व अन्य मंदिर।", "The Dwadash Jyotirlingas, Shakti Peethas, Ashta Vinayaka and the great Vaishnavite and other temples of Bharat.")}
      />

      <section className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-16">
        {active ? (
          <>
            <button onClick={() => setParams({})} data-testid="temples-back" className="mb-8 inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-saffron">
              <ArrowLeft className="h-4 w-4" /> {pick("सभी परिक्रमाएँ", "All circuits")}
            </button>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="circuit-temples-grid">
              {activeTemples.map((t) => <TempleCard key={t.slug} t={t} />)}
              {activeTemples.length === 0 && <p className="col-span-full py-16 text-center text-ink-3">{pick("इस परिक्रमा में अभी कोई मंदिर नहीं।", "No temples in this circuit yet.")}</p>}
            </div>
          </>
        ) : (
          <>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="circuit-cards-grid">
              {shownCircuits.map((c) => (
                <CircuitCard key={c.id} c={c} cover={c.cover || circuitMeta[c.id]?.cover} count={circuitMeta[c.id]?.count || 0} pick={pick} />
              ))}
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              <Link to="/pilgrimage" data-testid="temples-pilgrimage-link"
                className="group flex items-center gap-4 overflow-hidden rounded-xl border border-line bg-[var(--surface)] p-5 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg md:p-6">
                <Compass className="h-8 w-8 shrink-0 text-saffron" />
                <div className="min-w-0 flex-1">
                  <p className="font-serif-latin text-lg text-ink">{pick("तीर्थ एवं यात्रा", "Pilgrimage & Tirthas")}</p>
                  <p className="mt-0.5 text-sm text-ink-2">{pick("भारत के पवित्र तीर्थ, धाम एवं यात्रा-मार्ग।", "Sacred tirthas, dhams and pilgrimage routes of Bharat.")}</p>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-ink-3 transition-transform group-hover:translate-x-1 group-hover:text-saffron" />
              </Link>
              <Link to="/amnaya-peethas" data-testid="temples-amnaya-link"
                className="group flex items-center gap-4 overflow-hidden rounded-xl border border-line bg-[var(--surface)] p-5 transition-all duration-300 hover:border-[var(--gold)] hover:shadow-lg md:p-6">
                <Landmark className="h-8 w-8 shrink-0 text-saffron" />
                <div className="min-w-0 flex-1">
                  <p className="font-serif-latin text-lg text-ink">{pick("चार आम्नाय पीठ", "Four Amnaya Peethas")}</p>
                  <p className="mt-0.5 text-sm text-ink-2">{pick("आदि शंकराचार्य द्वारा स्थापित चार मठ।", "The four mathas established by Adi Shankaracharya.")}</p>
                </div>
                <ArrowRight className="h-5 w-5 shrink-0 text-ink-3 transition-transform group-hover:translate-x-1 group-hover:text-saffron" />
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
